import React from 'react';
import { Helmet } from 'react-helmet';

import { AppState } from '@ttstr/reducers';
import { Product, Variant, VariantShippingDetail } from '@ttstr/api/products';
import { useProductDetailLink, useProductDetails, useShopConfig } from '@ttstr/components';
import { useShallowEqualSelector } from '@ttstr/utils';

interface Props {
  product: Product;
}

/**
 * Generates ProductGroup structured data (JSON-LD) with per-variant Product entries.
 *
 * Aligned with Google's merchant listing guidelines:
 * https://developers.google.com/search/docs/appearance/structured-data/merchant-listing
 * https://developers.google.com/search/docs/advanced/structured-data/product#product
 */

// --- Schema.org type definitions ---

interface OfferSchema {
  '@type': 'Offer';
  url: string;
  price: number;
  priceCurrency: string;
  itemCondition: string;
  availability: string;
  seller: OrganizationSchema;
  shippingDetails?: OfferShippingDetailsSchema[];
}

interface ShippingRateSchema {
  '@type': 'MonetaryAmount';
  value?: number;
  minValue?: number;
  maxValue?: number;
  currency: string;
}

interface DefinedRegionSchema {
  '@type': 'DefinedRegion';
  addressCountry: string[];
}

interface OfferShippingDetailsSchema {
  '@type': 'OfferShippingDetails';
  shippingRate: ShippingRateSchema;
  shippingDestination: DefinedRegionSchema;
}

interface OrganizationSchema {
  '@type': 'Organization';
  name: string;
  url: string;
}

interface BrandSchema {
  '@type': 'Brand';
  name: string;
}

interface VariantProductSchema {
  '@type': 'Product';
  name: string;
  sku?: string;
  gtin14?: string;
  image: string;
  offers: OfferSchema;
}

interface ProductGroupSchema {
  '@context': 'https://schema.org/';
  '@type': 'ProductGroup';
  name: string;
  description?: string;
  url: string;
  image?: string;
  productGroupID: string;
  brand?: BrandSchema;
  hasVariant: VariantProductSchema[];
}

// --- Constants ---

/**
 * Full list of shipping-eligible country codes, matching the backend's
 * HasAddress::AVAILABLE_COUNTRIES minus RU/XXX.
 *
 * When the backend sends country_codes: ["*"] it means "worldwide" —
 * we expand it to this list for the schema.org OfferShippingDetails.
 */
const ALL_SHIPPING_COUNTRIES = [
  'DE',
  'CH',
  'AT',
  'LU',
  'FR',
  'NL',
  'BE',
  'DK',
  'PL',
  'CZ',
  'AC',
  'AD',
  'AE',
  'AF',
  'AG',
  'AI',
  'AL',
  'AM',
  'AN',
  'AO',
  'AQ',
  'AR',
  'AS',
  'AU',
  'AW',
  'AX',
  'AZ',
  'BA',
  'BB',
  'BD',
  'BF',
  'BG',
  'BH',
  'BI',
  'BJ',
  'BL',
  'BM',
  'BN',
  'BO',
  'BQ',
  'BR',
  'BS',
  'BT',
  'BV',
  'BW',
  'BY',
  'BZ',
  'CA',
  'CC',
  'CD',
  'CF',
  'CG',
  'CI',
  'CK',
  'CL',
  'CM',
  'CN',
  'CO',
  'CP',
  'CR',
  'CU',
  'CV',
  'CW',
  'CX',
  'CY',
  'DG',
  'DJ',
  'DM',
  'DO',
  'DZ',
  'EA',
  'EC',
  'EE',
  'EG',
  'EH',
  'ER',
  'ES',
  'ET',
  'FI',
  'FJ',
  'FK',
  'FM',
  'FO',
  'GA',
  'GB',
  'GD',
  'GE',
  'GF',
  'GG',
  'GH',
  'GI',
  'GL',
  'GM',
  'GN',
  'GP',
  'GQ',
  'GR',
  'GS',
  'GT',
  'GU',
  'GW',
  'GY',
  'HK',
  'HM',
  'HN',
  'HR',
  'HT',
  'HU',
  'IC',
  'ID',
  'IE',
  'IL',
  'IM',
  'IN',
  'IO',
  'IQ',
  'IR',
  'IS',
  'IT',
  'JE',
  'JM',
  'JO',
  'JP',
  'KE',
  'KG',
  'KH',
  'KI',
  'KM',
  'KN',
  'KP',
  'KR',
  'KW',
  'KY',
  'KZ',
  'LA',
  'LB',
  'LC',
  'LI',
  'LK',
  'LR',
  'LS',
  'LT',
  'LV',
  'LY',
  'MA',
  'MC',
  'MD',
  'ME',
  'MF',
  'MG',
  'MH',
  'MK',
  'ML',
  'MM',
  'MN',
  'MO',
  'MP',
  'MQ',
  'MR',
  'MS',
  'MT',
  'MU',
  'MV',
  'MW',
  'MX',
  'MY',
  'MZ',
  'NA',
  'NC',
  'NE',
  'NF',
  'NG',
  'NI',
  'NO',
  'NP',
  'NR',
  'NU',
  'NZ',
  'OM',
  'PA',
  'PE',
  'PF',
  'PG',
  'PH',
  'PK',
  'PM',
  'PN',
  'PR',
  'PS',
  'PT',
  'PW',
  'PY',
  'QA',
  'QO',
  'RE',
  'RO',
  'RS',
  'RW',
  'SA',
  'SB',
  'SC',
  'SD',
  'SE',
  'SG',
  'SH',
  'SI',
  'SJ',
  'SK',
  'SL',
  'SM',
  'SN',
  'SO',
  'SR',
  'SS',
  'ST',
  'SV',
  'SX',
  'SY',
  'SZ',
  'TA',
  'TC',
  'TD',
  'TF',
  'TG',
  'TH',
  'TJ',
  'TK',
  'TL',
  'TM',
  'TN',
  'TO',
  'TR',
  'TT',
  'TV',
  'TW',
  'TZ',
  'UA',
  'UG',
  'UM',
  'US',
  'UY',
  'UZ',
  'VA',
  'VC',
  'VE',
  'VG',
  'VI',
  'VN',
  'VU',
  'WF',
  'WS',
  'XK',
  'YE',
  'YT',
  'ZA',
  'ZM',
  'ZW',
];

// --- Helpers ---

const SELLER: OrganizationSchema = {
  '@type': 'Organization',
  name: 'tickettoaster GmbH',
  url: 'https://www.tickettoaster.de',
};

/**
 * Simplified availability check aligned with product.jbuilder:
 *   remaining_for(channel) > 0 → InStock, else SoldOut
 */
function getVariantAvailability(variant: Variant): string {
  if (variant.remaining_for_online > 0) {
    return 'https://schema.org/InStock';
  }
  return 'https://schema.org/SoldOut';
}

function stripHtml(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

// --- Component ---

const ProductMarkup: React.FC<Props> = ({ product }) => {
  const { canonicalUrl } = useShopConfig();
  const productDetailLink = useProductDetailLink();
  const { currency } = useShallowEqualSelector(mapStateToProps);
  const { brands } = useProductDetails();

  const variants: Variant[] = React.useMemo(() => {
    if (Array.isArray(product.online_variants_attributes)) return product.online_variants_attributes;
    return Object.values(product.online_variants_attributes);
  }, [product]);

  const productUrl = productDetailLink(product, { canonicalUrl });
  const priceCurrency = currency ?? 'EUR';

  const jsonLd = React.useMemo<ProductGroupSchema>(() => {
    const schema: ProductGroupSchema = {
      '@context': 'https://schema.org/',
      '@type': 'ProductGroup',
      name: [product.supertitle, product.title, product.subtitle].filter(Boolean).join(' - '),
      url: productUrl,
      productGroupID: String(product.id),
      hasVariant: [],
    };

    // Description (strip HTML)
    if (product.description) {
      schema.description = stripHtml(product.description);
    }

    // Product image — jbuilder uses product.image only
    if (product.image?.url) {
      schema.image = canonicalUrl + product.image.url;
    } else if (product.hero_image?.url) {
      schema.image = canonicalUrl + product.hero_image.url;
    }

    // Brand — aligned with product.jbuilder: brands[0, 1] — first brand only
    if (product.brand_ids?.length) {
      const firstBrand = brands[product.brand_ids[0]];
      if (firstBrand) {
        schema.brand = {
          '@type': 'Brand',
          name: firstBrand.title,
        };
      }
    }

    // Per-variant Product entries (aligned with product.jbuilder hasVariant pattern)
    schema.hasVariant = variants.map((variant) => {
      // Image: variant image → product image → product hero_image (always resolve to something)
      let variantImage: string;
      if (variant.image?.url) {
        variantImage = canonicalUrl + variant.image.url;
      } else if (product.image?.url) {
        variantImage = canonicalUrl + product.image.url;
      } else if (product.hero_image?.url) {
        variantImage = canonicalUrl + product.hero_image.url;
      } else {
        variantImage = '';
      }

      const variantName = variant.title || product.title;

      const variantProduct: VariantProductSchema = {
        '@type': 'Product',
        name: variantName,
        image: variantImage,
        offers: {
          '@type': 'Offer',
          priceCurrency,
          price: Number(Number(variant.total_price ?? variant.price ?? 0).toFixed(2)),
          itemCondition: 'https://schema.org/NewCondition',
          url: productUrl + '#variant-' + variant.id,
          availability: getVariantAvailability(variant),
          seller: SELLER,
        },
      };

      if ((variant as any).sku) {
        variantProduct.sku = (variant as any).sku;
      }
      if (!variantProduct.sku) {
        variantProduct.sku = String(variant.id);
      }
      if (variant.ean) {
        variantProduct.gtin14 = variant.ean;
      }

      // Shipping details — mirrors product.jbuilder OfferShippingDetails pattern.
      // Backend sends ["*"] as country_codes for worldwide shipping to save bandwidth;
      // we expand it to the full ALL_SHIPPING_COUNTRIES list for schema.org.
      const variantShippingDetails: any[] | VariantShippingDetail = product.shipping_details_schema?.[variant.id] || [];
      if (variantShippingDetails?.length > 0) {
        variantProduct.offers.shippingDetails = variantShippingDetails.map?.((sd) => {
          const shippingRate: ShippingRateSchema = {
            '@type': 'MonetaryAmount',
            currency: sd.currency,
          };
          if (sd.max_price != null) {
            shippingRate.minValue = sd.min_price;
            shippingRate.maxValue = sd.max_price;
          } else {
            shippingRate.value = sd.price ?? 0;
          }
          const countryCodes =
            sd.country_codes.length === 1 && sd.country_codes[0] === '*' ? ALL_SHIPPING_COUNTRIES : sd.country_codes;
          return {
            '@type': 'OfferShippingDetails' as const,
            shippingRate,
            shippingDestination: {
              '@type': 'DefinedRegion' as const,
              addressCountry: countryCodes,
            },
          };
        });
      }

      return variantProduct;
    });

    return schema;
  }, [product, variants, canonicalUrl, productUrl, priceCurrency, brands]);

  const pageTitle = [product.supertitle, product.title, product.subtitle].filter(Boolean).join(' - ');
  const plainDescription = product.description ? stripHtml(product.description).substring(0, 200) : '';

  // Google requires name, image, and offers for Product structured data.
  // Skip the JSON-LD script entirely when any required property is missing.
  const hasAnyImage =
    Boolean(product.image?.url) || Boolean(product.hero_image?.url) || variants.some((v) => Boolean(v.image?.url));
  const hasRequiredStructuredData = Boolean(jsonLd.name) && hasAnyImage && jsonLd.hasVariant.length > 0;

  React.useEffect(() => {
    if (!hasRequiredStructuredData) {
      const missing: string[] = [];
      if (!jsonLd.name) missing.push('name');
      if (!hasAnyImage) missing.push('image');
      if (jsonLd.hasVariant.length === 0) missing.push('offers (no variants)');
      console.warn(
        `[ProductMarkup] Product structured data (JSON-LD) not rendered for product ${
          product.id
        }: missing required properties: ${missing.join(', ')}`
      );
    }
  }, [hasRequiredStructuredData, jsonLd.name, hasAnyImage, jsonLd.hasVariant.length, product.id]);

  return (
    <Helmet>
      <title>{pageTitle}</title>

      {plainDescription && <meta name="description" content={plainDescription} />}

      <meta property="og:title" content={product.title} />
      {plainDescription && <meta property="og:description" content={plainDescription} />}
      <meta property="og:url" content={productUrl} />
      {product.image?.url && <meta property="og:image" content={canonicalUrl + product.image.url} />}

      {hasRequiredStructuredData && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      <link rel="canonical" href={productUrl} />
    </Helmet>
  );
};

const mapStateToProps = (state: AppState) => {
  const { settings } = state.Settings;
  return {
    currency: settings.config_currency,
  };
};

export default React.memo(ProductMarkup);
